<template>
  <Teleport to="body">
    <!-- disabled transitions: were making content load choppy/distracting; was not adding value -->
    <!-- <Transition
      enter-active-class="tw-transition tw-duration-200 tw-ease-out"
      enter-from-class="tw-opacity-0"
      enter-to-class="tw-opacity-100"
      leave-active-class="tw-transition tw-duration-150 tw-ease-in"
      leave-from-class="tw-opacity-100"
      leave-to-class="tw-opacity-0"
    > -->
      <div
        v-if="isOpen"
        class="tw-fixed tw-inset-0 tw-z-[3000] tw-flex tw-items-end sm:tw-items-center tw-justify-center tw-p-4"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || 'Modal'"
      >
        <div
          class="tw-absolute tw-inset-0 tw-bg-slate-900/40 tw-backdrop-blur-sm"
          @click="onBackdropClick"
        ></div>

        <!-- disabled transitions: were making content load choppy/distracting; was not adding value -->
        <!-- <Transition
          enter-active-class="tw-transition tw-duration-200 tw-ease-out"
          enter-from-class="tw-opacity-0 tw-translate-y-3 sm:tw-translate-y-0 sm:tw-scale-95"
          enter-to-class="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
          leave-active-class="tw-transition tw-duration-150 tw-ease-in"
          leave-from-class="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
          leave-to-class="tw-opacity-0 tw-translate-y-2 sm:tw-translate-y-0 sm:tw-scale-95"
        > -->
          <div
            v-if="isOpen"
            class="tw-relative tw-flex tw-w-full tw-max-h-[85vh] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-shadow-slate-900/20 tw-ring-1 tw-ring-slate-200"
            :class="maxWidthClass"
          >
            <div
              v-if="hasHeader"
              class="tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-slate-200 tw-to-transparent"
            ></div>
            <div
              v-if="hasHeader"
              class="tw-absolute tw-inset-x-0 tw-top-0 tw-h-12 tw-bg-gradient-to-b tw-from-slate-50 tw-to-transparent"
            ></div>

            <div
              v-if="hasHeader"
              class="tw-relative tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4"
            >
              <div class="tw-min-w-0">
                <div v-if="$slots.title" class="tw-text-xl tw-font-semibold tw-text-slate-900 ">
                  <slot name="title"></slot>
                </div>
                <div v-else-if="title" class="tw-text-xl tw-font-semibold tw-text-slate-900">{{ title }}</div>
                <div v-if="$slots.subtitle" class="tw-text-base tw-text-slate-600 tw-mt-0.5">
                  <slot name="subtitle"></slot>
                </div>
                <div v-else-if="subtitle" class="tw-text-base tw-text-slate-600 tw-mt-0.5">{{ subtitle }}</div>
              </div>

              <button
                v-if="hasCloseButton"
                type="button"
                class="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/70 tw-text-slate-700 hover:tw-bg-slate-100 hover:tw-border-slate-300 hover:tw-text-slate-900"
                aria-label="Close"
                @click="requestClose"
              >
                <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
              </button>
            </div>

            <div
              class="tw-relative tw-overflow-auto tw-px-5 tw-py-4"
              :class="hasHeader ? 'tw-max-h-[calc(85vh-64px)]' : 'tw-max-h-[85vh]'"
            >
              <slot v-if="slots.content" name="content" :close="requestClose"></slot>
              <slot v-else></slot>
            </div>
          </div>
        <!-- </Transition> -->
      </div>
    <!-- </Transition> -->
  </Teleport>
</template>

<script lang="ts" setup>
import { Teleport, Transition, computed, onBeforeUnmount, useSlots, watch } from 'vue';
import { useModalStack } from './useModalStack';

const isOpen = defineModel<boolean>('isOpen', { default: false });

const props = withDefaults(
  defineProps<{
    modalId?: string;
    title?: string;
    subtitle?: string;
    ariaLabel?: string;
    hasCloseButton?: boolean;
    isClosableOnBackdrop?: boolean;
    isClosableOnEsc?: boolean;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  }>(),
  {
    modalId: () => `modal-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    hasCloseButton: true,
    isClosableOnBackdrop: true,
    isClosableOnEsc: true,
    maxWidth: '6xl',
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const slots = useSlots();
const { pushModal, removeModal, isTopModal } = useModalStack();

const hasHeader = computed(() => {
  return (
    !!props.hasCloseButton ||
    !!props.title ||
    !!props.subtitle ||
    !!slots.title ||
    !!slots.subtitle
  );
});

const maxWidthClass = computed(() => {
  const map: Record<string, string> = {
    sm: 'tw-max-w-sm',
    md: 'tw-max-w-md',
    lg: 'tw-max-w-lg', //32rem
    xl: 'tw-max-w-xl', // 36rem (+4rem)
    '2xl': 'tw-max-w-2xl', // 42rem (+6rem)
    '3xl': 'tw-max-w-3xl', // 48rem (+6rem)
    '4xl': 'tw-max-w-4xl', // 56rem (+8rem)
    '5xl': 'tw-max-w-5xl', // 64rem (+8rem)
    '6xl': 'tw-max-w-[70vw]', // 72rem (+8rem)
    '7xl': 'tw-max-w-[80vw]', // 80rem
  };
  return map[props.maxWidth] || 'tw-max-w-6xl';
});

/** User-initiated close (X button, backdrop, Esc, slot close()) - only top modal reacts; emits @close */
function requestClose() {
  if (!isOpen.value) return;
  if (!isTopModal(props.modalId)) return;
  isOpen.value = false;
  emit('close');
}

function onBackdropClick() {
  if (!props.isClosableOnBackdrop) return;
  requestClose();
}

let isStackRegistered = false;

watch(
  isOpen,
  (open) => {
    if (open && !isStackRegistered) {
      pushModal({
        id: props.modalId,
        isOpen: true,
        close: requestClose,
        isClosableOnEsc: () => props.isClosableOnEsc,
      });
      isStackRegistered = true;
    } else if (!open && isStackRegistered) {
      removeModal(props.modalId);
      isStackRegistered = false;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (isStackRegistered) {
    removeModal(props.modalId);
    isStackRegistered = false;
  }
});

// Imperative escape hatch: lets a parent close the modal via a template ref
// (`<Modal ref="modalRef" />` then `modalRef.value.close()`). The normal way
// to close is to flip `v-model:isOpen` to false; only reach for this when the
// parent doesn't own the open state but still needs to force a close.
defineExpose({
  close: requestClose,
});
</script>
